import { FC, useEffect, useRef, useState } from 'react';
import {
  AssignedSubmissionTooltip,
  AssignedSubmissionWithTooltip,
  ModernAssignedSubmission,
  ModernCheckIcon,
  ModernChevronIcon,
  ModernDropdownContainer,
  ModernDropdownContent,
  ModernDropdownMenu,
  ModernDropdownSection,
  ModernDropdownTrigger,
  ModernOptionContent,
  ModernOptionText,
  ModernOtherSubmission,
  ModernOtherSubmissionsGrid,
  ModernPlaceholderText,
  ModernSectionTitle,
  ModernStatusIcon,
  ModernSubmissionText,
  SubmissionTooltip,
  SubmissionWithTooltip
} from '../styles';

interface DropdownOption {
  value: string;
  label: string;
  icon?: string;
  status?: string;
  applicantName?: string;
  documentCount?: number;
}

interface ModernSubmissionDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  assignedEntryId?: string;
  placeholder?: string;
  disabled?: boolean;
  width?: string;
  id?: string;
}

const ModernSubmissionDropdown: FC<ModernSubmissionDropdownProps> = ({
  value,
  onChange,
  options,
  assignedEntryId,
  placeholder = 'Select submission...',
  disabled = false,
  width = '180px',
  id
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideInteraction = (event: Event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleWindowBlur = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideInteraction, true);
      document.addEventListener('click', handleOutsideInteraction, true);
      document.addEventListener('touchstart', handleOutsideInteraction, true);
      window.addEventListener('blur', handleWindowBlur);
      window.addEventListener('focus', handleOutsideInteraction, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction, true);
      document.removeEventListener('click', handleOutsideInteraction, true);
      document.removeEventListener('touchstart', handleOutsideInteraction, true);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleOutsideInteraction, true);
    };
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        const currentIndex = options.findIndex((opt) => opt.value === value);
        const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
        onChange(options[nextIndex].value);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        const currentIndex = options.findIndex((opt) => opt.value === value);
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
        onChange(options[prevIndex].value);
      }
    }
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent, optionValue: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onChange(optionValue);
      setIsOpen(false);
    }
  };

  return (
    <ModernDropdownContainer ref={dropdownRef} style={{ width }} id={id}>
      <ModernDropdownTrigger
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        $isOpen={isOpen}
        $disabled={disabled}
      >
        <ModernDropdownContent>
          <ModernPlaceholderText>Submission</ModernPlaceholderText>
        </ModernDropdownContent>
        <ModernChevronIcon $isOpen={isOpen}>
          <img src="/assets/images/dropdown.svg" alt="dropdown" />
        </ModernChevronIcon>
      </ModernDropdownTrigger>

      {isOpen && !disabled && (
        <ModernDropdownMenu role="listbox">
          {/* Assigned Submission Section */}
          {assignedEntryId && (
            <ModernDropdownSection>
              <ModernSectionTitle>Assigned submission</ModernSectionTitle>
              {(() => {
                const assignedOption = options.find((opt) => opt.value === assignedEntryId);
                if (assignedOption) {
                  return (
                    <AssignedSubmissionWithTooltip>
                      <ModernAssignedSubmission
                        role="option"
                        tabIndex={0}
                        aria-selected={value === assignedOption.value}
                        onClick={() => {
                          onChange(assignedOption.value);
                          setIsOpen(false);
                        }}
                        onKeyDown={(e) => handleOptionKeyDown(e, assignedOption.value)}
                        $selected={value === assignedOption.value}
                      >
                        <ModernOptionContent>
                          <div>
                            <ModernSubmissionText>
                              {assignedOption.applicantName || 'Unknown'}
                            </ModernSubmissionText>
                            <ModernOptionText>{assignedOption.label}</ModernOptionText>
                          </div>
                          {assignedOption.icon && (
                            <ModernStatusIcon src={assignedOption.icon} alt="status" />
                          )}
                        </ModernOptionContent>
                        {value === assignedOption.value && (
                          <ModernCheckIcon>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M13.5 4.5L6 12L2.5 8.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </ModernCheckIcon>
                        )}
                      </ModernAssignedSubmission>
                      {assignedOption.documentCount !== undefined && (
                        <AssignedSubmissionTooltip>
                          {assignedOption.documentCount} Documents
                        </AssignedSubmissionTooltip>
                      )}
                    </AssignedSubmissionWithTooltip>
                  );
                }
                return null;
              })()}
            </ModernDropdownSection>
          )}

          {/* Other Submissions Section */}
          <ModernDropdownSection>
            <ModernSectionTitle id="other-submissions-label">Other submissions</ModernSectionTitle>
            <ModernOtherSubmissionsGrid>
              {options
                .filter((option) => option.value !== assignedEntryId)
                .map((option) => (
                  <SubmissionWithTooltip key={option.value}>
                    <ModernOtherSubmission
                      role="option"
                      tabIndex={0}
                      aria-selected={value === option.value}
                      onClick={() => {
                        onChange(option.value);
                        setIsOpen(false);
                      }}
                      onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
                      $selected={value === option.value}
                    >
                      <ModernOptionContent>
                        <div>
                          <ModernSubmissionText>
                            {option.applicantName || 'Unknown'}
                          </ModernSubmissionText>
                          <ModernOptionText>{option.label}</ModernOptionText>
                        </div>
                        {option.icon && <ModernStatusIcon src={option.icon} alt="status" />}
                      </ModernOptionContent>
                      {value === option.value && (
                        <ModernCheckIcon>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M13.5 4.5L6 12L2.5 8.5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </ModernCheckIcon>
                      )}
                    </ModernOtherSubmission>
                    {option.documentCount !== undefined && (
                      <SubmissionTooltip>{option.documentCount} Documents</SubmissionTooltip>
                    )}
                  </SubmissionWithTooltip>
                ))}
            </ModernOtherSubmissionsGrid>
          </ModernDropdownSection>
        </ModernDropdownMenu>
      )}
    </ModernDropdownContainer>
  );
};

export default ModernSubmissionDropdown;
